import {
  Client, BrowserZmlClient
} from "@zappar/zw-api-client";
import { MediaType, ExternalAssetSource } from "../../types";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams } from "../base/types";
import { Model3DAsset } from "./assets";
import { PolypizzaAsset, PolyPizzaCategory } from "./types";


/**
 * PolyPizza Provider
 *
 * Provides read-only access to Poly Pizza's library of low poly 3D models.
 * https://poly.pizza/
 * https://poly.pizza/docs/api/v1.1
 *
 * The Poly Pizza API does not allow direct browser requests (CORS) and requires
 * an API key. Both concerns are handled by routing all requests through the
 * server-side proxy at `proxyUrl`, which injects credentials and forwards the
 * response back to the client.
 */
export class PolyPizzaProvider extends BaseProvider {
  private client: BrowserZmlClient;
  private static readonly POLYPIZZA_API_BASE = 'https://api.poly.pizza/v1.1';

  constructor(apiClient: Client) {
    super({
      id: ExternalAssetSource.Polypizza,
      name: 'polypizza',
      displayName: 'Poly Pizza',
      description: 'Every low poly model you could ever need',
      baseUrl: '', // Not used anymore
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [ MediaType.Model3D ]
      }
    });

    this.client = apiClient.zml;
  }

  getCategories(): Promise<string[]> {
    return Promise.resolve(Object.keys(PolyPizzaCategory).filter(key => isNaN(Number(key))));
  }

  /**
   * Fetch a single page from the Poly Pizza API and return parsed assets.
   */
  private async fetchPage(url: string): Promise<IAsset[]> {
    let response: any;
    try {
      response = await this.client.fetchViaExternalProxy(url, 'polypizza');
    } catch (error) {
      console.error('Error fetching from Poly Pizza API:', error);
      return [];
    }

    if (!response?.results) return [];

    return response.results.map((asset: PolypizzaAsset) => new Model3DAsset(asset));
  }

  /**
   * Search for 3D model assets in Poly Pizza.
   *
   * - With a keyword: searches `/search/{keyword}` with optional Category/Limit/Page.
   * - With a category (no keyword): searches `/search?Category=...`.
   * - With neither: fetches all models by querying both License=0 (CC-BY) and
   *   License=1 (CC0) in parallel for the given page, then merges the results.
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const base = PolyPizzaProvider.POLYPIZZA_API_BASE;
    const limit = params.limit ?? 32;
    const page = params.page ?? 0;

    if (params.query) {
      // Keyword search — Category is optional
      const qp = new URLSearchParams({ Limit: String(limit), Page: String(page) });
      if (params.category != null) qp.set('Category', String(PolyPizzaCategory[params.category as keyof typeof PolyPizzaCategory]));
      return this.fetchPage(`${base}/search/${encodeURIComponent(params.query)}?${qp}`);
    }

    if (params.category != null) {
      // Filter-only search — Category satisfies the required-filter constraint
      const qp = new URLSearchParams({ Category: String(PolyPizzaCategory[params.category as keyof typeof PolyPizzaCategory]), Limit: String(limit), Page: String(page) });
      return this.fetchPage(`${base}/search?${qp}`);
    }

    // No query, no category — browse all models by fetching both license types in parallel
    const qpCC_BY = new URLSearchParams({ License: '0', Limit: String(limit), Page: String(page) });
    const qpCC0   = new URLSearchParams({ License: '1', Limit: String(limit), Page: String(page) });
    const [ccBy, cc0] = await Promise.all([
      this.fetchPage(`${base}/search?${qpCC_BY}`),
      this.fetchPage(`${base}/search?${qpCC0}`),
    ]);
    return [...ccBy, ...cc0];
  }

  /**
   * Get a specific asset by ID
   */
  async getAssetById(id: string): Promise<IAsset> {
    const targetUrl = `${PolyPizzaProvider.POLYPIZZA_API_BASE}/model/${id}`;
    const response = await this.client.fetchViaExternalProxy(targetUrl, 'polypizza');

    if (!response) {
      throw new Error(`Asset with ID ${id} not found in Poly Pizza`);
    }
    return new Model3DAsset(response);
  }
}
